import { ref, computed } from 'vue';
import HttpClient from "project-blue-http-client";
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PRICES_URL, ASSETS_URL } from '@/utils/constants';
import { SUBSCRIPTION_INFO } from "@/composables/subscription";
dayjs.extend(isoWeek);

// Baseline start dates for Pricing Services (data collection began on 21/09/2026 = Week 39)
// We have no historical data before this date, so this acts as our earliest starting point.
export const PRICE_START_DATE_WEEKLY = '2026-09-21'; // Monday of W39
export const PRICE_START_DATE_MONTHLY = '2026-09';    // September 2026

// --- State Variables ---
// Store all the Pricing Services fetched from the database here
export const myPricingServices = ref([]);

// Scans fetched pricing services for the earliest entry date with non-empty data
export const getEarliestDataDate = (frequency) => {
  const isMonthly = frequency.toLowerCase() === 'monthly';
  let earliest = null;

  const services = myPricingServices.value || [];
  for (let i = 0; i < services.length; i++) {
    const item = services[i];
    if (item.priceFrequency && item.priceFrequency.toLowerCase() !== frequency.toLowerCase()) {
      continue;
    }
    const entries = item.entryValues;
    if (!entries || !entries.length) continue;

    for (let j = 0; j < entries.length; j++) {
      const entry = entries[j];
      const valStr = entry.entryValue != null ? String(entry.entryValue).trim() : '';
      if (valStr !== '' && valStr !== '-' && valStr !== 'null' && !isNaN(Number(valStr))) {
        const targetDate = entry.entryWeek || (isMonthly ? null : entry.createdAt);
        if (targetDate) {
          const d = isMonthly
            ? dayjs(targetDate).startOf('month')
            : dayjs(targetDate).startOf('isoWeek');
          if (d.isValid() && d.year() >= 2020) {
            if (!earliest || d.isBefore(earliest)) {
              earliest = d;
            }
          }
        }
      }
    }
  }

  // Fallback to baseline constants if no entries found in data
  if (!earliest) {
    const fallbackStr = isMonthly ? PRICE_START_DATE_MONTHLY : PRICE_START_DATE_WEEKLY;
    earliest = isMonthly ? dayjs(fallbackStr).startOf('month') : dayjs(fallbackStr).startOf('isoWeek');
  }

  return earliest;
};

// Scans all weekly pricing services to find months that actually have data,
// and returns the last 3 months (or fewer if fewer than 3 months exist).
export const getMonthlyAverageHistoryDates = () => {
  const monthsWithData = new Set();
  const services = myPricingServices.value || [];

  for (let i = 0; i < services.length; i++) {
    const item = services[i];
    // Only check weekly items since monthly averages are derived from weekly entries
    if (item.priceFrequency && item.priceFrequency.toLowerCase() !== 'weekly') continue;

    const entries = item.entryValues;
    if (!entries || !entries.length) continue;

    for (let j = 0; j < entries.length; j++) {
      const entry = entries[j];
      const valStr = entry.entryValue != null ? String(entry.entryValue).trim() : '';
      if (valStr !== '' && valStr !== '-' && !isNaN(Number(valStr))) {
        const targetDate = entry.entryWeek || entry.createdAt;
        if (targetDate) {
          const d = dayjs(targetDate).startOf('isoWeek');
          if (d.isValid() && d.year() >= 2020) {
            monthsWithData.add(d.format('YYYY-MM'));
          }
        }
      }
    }
  }

  // Sort months chronologically (e.g. ['2026-09', '2026-10'])
  const sortedMonths = Array.from(monthsWithData).sort();

  // If no data exists at all, fallback to baseline start month
  if (sortedMonths.length === 0) {
    sortedMonths.push(PRICE_START_DATE_MONTHLY);
  }

  // Take up to the last 3 months that have data (e.g. Oct, Nov, Dec if 4 months exist)
  const last3Months = sortedMonths.slice(-3);

  return last3Months.map(monthStr => {
    const d = dayjs(monthStr, 'YYYY-MM');
    return {
      rawStr: monthStr,        // "2026-09"
      year: d.format('YYYY'),  // "2026"
      main: d.format('MMM'),   // "Sep"
    };
  });
};

// Function to extract formatted dates for column headers from earliest week/month with data up to latest completed week/month (excluding current week/month)
export const getHistoryDates = (frequency, count) => {
  // If Monthly Averages is selected, return the last 3 months that have data
  if (frequency && frequency.toLowerCase() === 'monthlyaverages') {
    return getMonthlyAverageHistoryDates();
  }

  const isMonthly = frequency.toLowerCase() === 'monthly';
  const labels = [];
  const today = dayjs();
  const unit = isMonthly ? 'month' : 'week';
  const startUnit = isMonthly ? 'month' : 'isoWeek';

  // End period: strictly the last completed week or month (excluding current ongoing week/month)
  const endPeriod = today.subtract(1, unit).startOf(startUnit);
  let startPeriod = getEarliestDataDate(frequency);

  // Safety: startPeriod should not be after endPeriod
  if (startPeriod.isAfter(endPeriod)) {
    startPeriod = endPeriod;
  }

  let curr = startPeriod.clone();
  while (curr.isBefore(endPeriod) || curr.isSame(endPeriod, startUnit)) {
    const rawStr = isMonthly ? curr.format('YYYY-MM') : curr.format('YYYY-MM-DD');
    labels.push({
      rawStr,
      year: curr.format('YYYY'),
      main: isMonthly ? curr.format('MMM') : `W${curr.isoWeek()}`,
      // sub: isMonthly ? '' : curr.format('DD/MM')
    });
    curr = curr.add(1, unit).startOf(startUnit);
  }

  // If a fixed count limit is requested (e.g. 12 periods for Data Table)
  if (count && labels.length > count) {
    return labels.slice(-count);
  }

  return labels;
};

// List of unique Supply Chains extracted from the fetched data
export const supplyChains = ref([]);

const mapEntriesByDate = (entries, frequency) => {
  const isMonthly = frequency.toLowerCase() === 'monthly';
  const valMap = {};
  const objMap = {};

  entries.forEach(entry => {
    const targetDate = entry.entryWeek || entry.createdAt;
    if (targetDate) {
      const d = dayjs(targetDate);
      // Key is the exact start of the week or start of the month
      const key = isMonthly ? d.startOf('month').format('YYYY-MM') : d.startOf('isoWeek').format('YYYY-MM-DD');

      if (valMap[key] === undefined) {
        valMap[key] = Number(entry.entryValue);
        objMap[key] = entry;
      }
    }
  });

  return { valMap, objMap };
};

// The currently selected tab (e.g., "EV & Battery")
export const activeSupplyChain = ref('');

// Filters
export const commodityFilter = ref(null);
export const regionFilter = ref(null);
// Track loading state
export const inProgress = ref(false);

// --- Data Table View States ---
// Determines if we are showing the default Dashboard or the Data Table
export const activeView = ref('Dashboard');
// Determines which frequency the Data Table is currently showing
export const dataTableFrequency = ref('Weekly');
// Options for the frequency select box
export const frequencyOptions = [
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Monthly Averages', value: 'MonthlyAverages' }
  // { label: 'Monthly', value: 'Monthly' }
];

export const SUPPLY_CHAIN_OPTS = [
  { label: "EV & Battery", value: "EV_BATTERY" },
  { label: "Steel & Alloy", value: "STEEL_ALLOY" },
  { label: "Aerospace & Defense", value: "AEROSPACE_DEFENSE" },
  {
    label: "Electronics & Semiconductors",
    value: "ELECTRONICS_SEMICONDUCTORS",
  },
  { label: "Solar, Wind & ESS", value: "SOLAR_WIND_ESS" },
  { label: "Future Basket", value: "FUTURE_BASKET" },
];

// --- Computed Options for Filters ---
export const commodityOptions = computed(() => {
  if (!myPricingServices.value) return [];

  const subscribedElements = SUBSCRIPTION_INFO.pricingServicesElements || [];

  // Extract all names arrays and flatten them
  const allNames = myPricingServices.value.flatMap(item => item.names || []);

  // Filter by subscription
  const filteredNames = allNames.filter(name =>
    subscribedElements.some(el => el.code?.toLowerCase() === name.toLowerCase())
  );

  // Format them nicely, but keep raw name as value
  const optionsMap = new Map();
  filteredNames.forEach(name => {
    const found = fetchedElements.value.find(e => e.element_Code?.toLowerCase() === name.toLowerCase());
    const label = found ? `(${found.element_Code}) ${found.name}` : name;

    if (!optionsMap.has(name)) {
      optionsMap.set(name, { label, value: name });
    }
  });

  const uniqueOptions = Array.from(optionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  return uniqueOptions;
});

export const regionOptions = computed(() => {
  if (!myPricingServices.value) return [];

  const subscribedElements = SUBSCRIPTION_INFO.pricingServicesElements || [];

  // Only consider items that have at least one subscribed commodity
  const subscribedItems = myPricingServices.value.filter(item => {
    if (!item.names || item.names.length === 0) return false;
    return item.names.some(name =>
      subscribedElements.some(el => el.code?.toLowerCase() === name.toLowerCase())
    );
  });

  // Extract all unique country codes/regions from those subscribed items
  const allRegions = subscribedItems.map(item => item.countryCode).filter(Boolean);

  const optionsMap = new Map();
  allRegions.forEach(regionCode => {
    // Attempt to find the country by its code
    const found = fetchedCountries.value.find(c =>
      (c.country)?.toLowerCase() === regionCode.toLowerCase()
    );

    // Format as "(CN) China" if found, else just "CN"
    const label = found ? `(${found.country}) ${found.name}` : regionCode;

    if (!optionsMap.has(regionCode)) {
      optionsMap.set(regionCode, { label, value: regionCode });
    }
  });

  const uniqueOptions = Array.from(optionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  return uniqueOptions;
});

// --- Fetch Data ---

// Store the dynamic list of elements from the API
export const fetchedElements = ref([]);

// Fetch elements directly from the backend rather than using static JSON
export const fetchElements = async () => {
  try {
    let client = new HttpClient(ASSETS_URL);
    const response = await client.get(`element/getElements`);

    // Check if data is directly returned or nested
    const data = response?.data ? response.data : response;

    if (data && Array.isArray(data)) {
      fetchedElements.value = data;
    }
  } catch (err) {
    console.error("Failed to fetch elements:", err);
  }
};

export const fetchedCountries = ref([]);
// Fetch country/regions directly from the backend rather than using static JSON
export const fetchCountries = async () => {
  try {
    let client = new HttpClient(ASSETS_URL);
    const response = await client.get(`country/getCountries`);

    // Check if data is directly returned or nested
    const data = response?.data ? response.data : response;

    if (data && Array.isArray(data)) {
      fetchedCountries.value = data;
    }
  } catch (err) {
    console.error("Failed to fetch countries:", err);
  }
};

export const fetchedProducts = ref([]);
// Fetch products directly from the backend to map productId to human-readable product name
export const fetchProducts = async () => {
  try {
    let client = new HttpClient(ASSETS_URL);
    const response = await client.get(`product/list`);

    const data = response?.data ? response.data : response;

    if (data && Array.isArray(data)) {
      fetchedProducts.value = data;
    }
  } catch (err) {
    console.warn("Could not fetch product list:", err?.message || err);
  }
};

export const fetchPricingServices = async () => {
  inProgress.value = true;
  try {
    // Make sure we have the elements data for formatting our filters
    if (fetchedElements.value.length === 0) {
      await fetchElements();
    }

    if (fetchedCountries.value.length === 0) {
      await fetchCountries();
    }

    if (fetchedProducts.value.length === 0) {
      await fetchProducts();
    }

    let client = new HttpClient(PRICES_URL);
    const response = await client.get(`PricingServices/GetMyPricingServices`);

    // Since project-blue-http-client's interceptor returns response.data directly,
    // the payload is likely in 'response' itself, but we check 'response.data' just in case.
    const data = response?.data ? response.data : response;
    console.log("DATA ==", data);
    if (data && Array.isArray(data)) {
      myPricingServices.value = data;

      // 2. Extracting unique Supply Chains
      // Here, we extract the 'supplyChain' array from each object using .flatMap()
      // If supplyChain is empty or undefined, we provide an empty array [] to avoid errors.
      const allSupplyChains = data.flatMap(item => item.supplyChain || []);

      // A Set is a JavaScript feature that removes duplicates.
      // We add .sort() so the tabs appear in alphabetical order.
      const uniqueSorted = [...new Set(allSupplyChains)].sort();

      // // Move 'FUTURE_BASKET' to the very end as requested
      // const futureBasketIndex = uniqueSorted.indexOf('FUTURE_BASKET');
      // if (futureBasketIndex > -1) {
      //   uniqueSorted.splice(futureBasketIndex, 1); // Remove it from its alphabetical position
      //   uniqueSorted.push('FUTURE_BASKET'); // Add it to the end
      // }

      supplyChains.value = uniqueSorted;

      // 3. Set a default active tab
      // Default to 'Commodity' so all records appear initially (ungrouped)
      activeSupplyChain.value = 'Commodity';
    }
  } catch (error) {
    console.error("Failed to fetch my pricing services", error);
  } finally {
    inProgress.value = false;
  }
};

// --- Filtered Data Helpers ---

/**
 * Helper to filter the pricing services based on user-selected dropdowns and tabs.
 * @param {string} frequency - "weekly" or "monthly"
 * @returns {Array} - The filtered list of pricing services
 */
const getFilteredServices = (frequency) => {
  if (!myPricingServices.value) return [];

  // Monthly Averages is derived from weekly entries
  const isMonthlyAvg = frequency?.toLowerCase() === 'monthlyaverages';
  const targetFrequency = isMonthlyAvg ? 'weekly' : frequency?.toLowerCase();

  // Filter by frequency (Weekly vs Monthly)
  let filtered = myPricingServices.value.filter(item =>
    (item.priceFrequency || 'weekly').toLowerCase() === targetFrequency
  );

  // Filter by supply chain tab if a specific one is selected
  if (activeSupplyChain.value !== 'Commodity') {
    filtered = filtered.filter(item => item.supplyChain?.includes(activeSupplyChain.value));
  }

  // Filter by Commodity dropdown
  if (commodityFilter.value) {
    filtered = filtered.filter(item => (item.names || []).includes(commodityFilter.value));
  }

  // Filter by Region dropdown
  if (regionFilter.value) {
    filtered = filtered.filter(item => item.countryCode === regionFilter.value);
  }

  return filtered;
};

/**
 * Transforms a raw pricing service item into a format ready for the Naive UI DataTable.
 * It maps values to specific periods, calculates trends, and computes Y-o-Y, W-o-W or M-o-M.
 * 
 * @param {Object} item - The raw pricing service item from the API
 * @param {string} frequency - "weekly", "monthly", or "MonthlyAverages"
 * @param {Array} globalDates - The dynamically generated calendar dates (e.g., from getHistoryDates)
 * @param {boolean} isDataTable - True if this is for the Data Table tab (needs 12 periods), False for Dashboard (needs 3 periods and YoY)
 * @returns {Object} - Formatted row data for the UI
 */
const processServiceItem = (item, frequency, globalDates, isDataTable = false) => {
  const isWeekly = frequency.toLowerCase() === 'weekly';
  const isMonthlyAvg = frequency.toLowerCase() === 'monthlyaverages';

  // 1. Sort entry values chronologically DESC (latest first) using entryWeek or createdAt
  const sortedEntries = [...(item.entryValues || [])].sort((a, b) => {
    const dateA = new Date(a.entryWeek || a.createdAt);
    const dateB = new Date(b.entryWeek || b.createdAt);
    return dateB - dateA; // Highest (newest) date first
  });

  // Map entries into a dictionary where the key is the formatted date (e.g. YYYY-MM-DD or YYYY-MM)
  const { valMap, objMap } = mapEntriesByDate(sortedEntries, frequency);

  // If calculating Monthly Averages, group weekly entries by month and compute their average
  const monthlyAveragesMap = {};
  if (isMonthlyAvg) {
    const monthlyGroups = {};
    (item.entryValues || []).forEach(entry => {
      const valStr = entry.entryValue != null ? String(entry.entryValue).trim() : '';
      if (valStr !== '' && valStr !== '-' && !isNaN(Number(valStr))) {
        const targetDate = entry.entryWeek || entry.createdAt;
        if (targetDate) {
          const d = dayjs(targetDate).startOf('isoWeek');
          if (d.isValid() && d.year() >= 2020) {
            const mKey = d.format('YYYY-MM');
            if (!monthlyGroups[mKey]) monthlyGroups[mKey] = [];
            monthlyGroups[mKey].push(Number(valStr));
          }
        }
      }
    });

    Object.keys(monthlyGroups).forEach(mKey => {
      const vals = monthlyGroups[mKey];
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      monthlyAveragesMap[mKey] = Number(avg.toFixed(2));
    });
  }

  // Format commodity name nicely and replace with Symbol/Code if available
  const formatCommodity = (names) => {
    if (!names || names.length === 0) return '-';

    const subscribedElements = SUBSCRIPTION_INFO.pricingServicesElements || [];

    // Only include commodities that the user is subscribed to
    let filteredNames = names.filter(name =>
      subscribedElements.some(el => el.code?.toLowerCase() === name.toLowerCase())
    );

    // Fallback just in case none matched but they have access to the record (e.g. bundle access)
    if (filteredNames.length === 0) {
      filteredNames = names;
    }

    return filteredNames.map(name => {
      const found = fetchedElements.value.find(e => e.element_Code?.toLowerCase() === name.toLowerCase());
      return found ? `${found.element_Code}` : name;
    }).join(', ');
  };

  // Resolve the human-readable product name with fallbacks
  const formatProductName = (serviceItem) => {
    if (serviceItem.productName) return serviceItem.productName;
    if (serviceItem.product?.name) return serviceItem.product.name;
    if (serviceItem.product_name) return serviceItem.product_name;

    if (serviceItem.productId && fetchedProducts.value.length > 0) {
      const match = fetchedProducts.value.find(p => p.id === serviceItem.productId);
      if (match?.name) return match.name;
    }

    return serviceItem.productCode || '-';
  };

  // Set up the base properties for the row
  const row = {
    id: item.id,
    commodity: formatCommodity(item.names),
    product: formatProductName(item),
    productCode: item.productCode || '-',
    units: item.currency && item.unit ? `${item.currency}/${item.unit}` : (item.unit || '-'),
  };

  if (isMonthlyAvg) {
    // For Monthly Averages trend sparkline, show the average values for displayed months
    row.trendLabels = globalDates.map(td => td.main);
    row.trendData = globalDates.map(td => monthlyAveragesMap[td.rawStr] ?? null);
  } else {
    // Extract sparkline (trend) data to show in the chart (last 24 weeks for weekly, last 12 months for monthly)
    const trendCount = isWeekly ? 24 : 12;
    const today = dayjs();
    const lastCompletedPeriod = isWeekly
      ? today.subtract(1, 'week').startOf('isoWeek')
      : today.subtract(1, 'month').startOf('month');

    let endPeriod = lastCompletedPeriod;
    if (sortedEntries.length > 0) {
      const latestDate = dayjs(sortedEntries[0].entryWeek || sortedEntries[0].createdAt);
      const latestPeriod = isWeekly ? latestDate.startOf('isoWeek') : latestDate.startOf('month');

      // If there is data for current/future period, end there
      if (latestPeriod.isAfter(lastCompletedPeriod)) {
        endPeriod = latestPeriod;
      } else if (latestPeriod.isBefore(lastCompletedPeriod.subtract(trendCount, isWeekly ? 'week' : 'month'))) {
        // If all data is older than the trend window, anchor to the latest available entry so historical data is visible
        endPeriod = latestPeriod;
      }
    }

    const trendDates = [];
    const earliestRawStr = globalDates[0]?.rawStr;
    for (let i = trendCount - 1; i >= 0; i--) {
      const d = endPeriod.subtract(i, isWeekly ? 'week' : 'month');
      const rawStr = isWeekly ? d.startOf('isoWeek').format('YYYY-MM-DD') : d.startOf('month').format('YYYY-MM');

      // Skip any periods prior to the earliest available date in globalDates
      if (earliestRawStr && rawStr < earliestRawStr) continue;

      const label = isWeekly ? `W${d.isoWeek()}` : d.format('MMM-YY');
      trendDates.push({ rawStr, label });
    }

    row.trendLabels = trendDates.map(td => td.label);
    row.trendData = trendDates.map(td => {
      const val = valMap[td.rawStr];
      return val !== undefined && val !== null ? Number(val) : null;
    });
  }

  // --- Logic for the Data Table View ---
  if (isDataTable) {
    if (isMonthlyAvg) {
      // Assign the computed monthly average for each date column
      globalDates.forEach((dateObj, i) => {
        const val = monthlyAveragesMap[dateObj.rawStr];
        row[dateObj.rawStr] = (val !== undefined && val !== null) ? val : '-';
        row[`period${i}`] = row[dateObj.rawStr];
      });
      return row;
    }

    // Assign price value for each date in globalDates
    globalDates.forEach(dateObj => {
      const val = valMap[dateObj.rawStr];
      row[dateObj.rawStr] = (val !== undefined && val !== null && val !== '') ? Number(val) : '-';
    });

    // Also populate period0, period1, etc. for backward compatibility
    for (let i = 0; i < globalDates.length; i++) {
      const val = valMap[globalDates[globalDates.length - 1 - i].rawStr];
      row[`period${i}`] = (val !== undefined && val !== null && val !== '') ? Number(val) : '-';
    }
    return row;
  }

  // --- Logic for the Dashboard View (Dynamic Periods + WoW/MoM/YoY calculations) ---

  // Assign price value for each date in globalDates
  globalDates.forEach(dateObj => {
    const val = valMap[dateObj.rawStr];
    row[dateObj.rawStr] = (val !== undefined && val !== null && val !== '') ? Number(val) : '-';
  });

  // Fallbacks for backward compatibility
  if (globalDates.length >= 3) {
    const d0 = globalDates[globalDates.length - 3].rawStr;
    const d1 = globalDates[globalDates.length - 2].rawStr;
    const d2 = globalDates[globalDates.length - 1].rawStr;
    if (isWeekly) {
      row.week2 = row[d0];
      row.week1 = row[d1];
      row.lastWeek = row[d2];
    } else {
      row.month2 = row[d0];
      row.month1 = row[d1];
      row.lastMonth = row[d2];
    }
  }

  // Find the latest entry among globalDates (latest date first)
  let latestEntry = null;
  for (let i = globalDates.length - 1; i >= 0; i--) {
    if (objMap[globalDates[i].rawStr]) {
      latestEntry = objMap[globalDates[i].rawStr];
      break;
    }
  }
  if (!latestEntry && sortedEntries.length > 0) {
    latestEntry = sortedEntries[0];
  }
  row.rationalComment = latestEntry ? latestEntry.comment : null;

  // Format display date (DD/MM) from latest entry
  let dateStr = '-';
  if (latestEntry) {
    const targetDate = latestEntry.createdAt || latestEntry.entryWeek;
    if (targetDate) {
      dateStr = dayjs(targetDate).format('DD/MM');
    }
  }
  row.date = dateStr;

  // Find latest two non-null prices in globalDates to compute WoW / MoM
  let latestVal = null;
  let priorVal = null;
  for (let i = globalDates.length - 1; i >= 0; i--) {
    const v = valMap[globalDates[i].rawStr];
    if (v !== undefined && v !== null && v !== '') {
      if (latestVal === null) {
        latestVal = Number(v);
      } else if (priorVal === null) {
        priorVal = Number(v);
        break;
      }
    }
  }

  if (isWeekly) {
    if (latestVal !== null && priorVal !== null && priorVal !== 0) {
      row.wow = (((latestVal / priorVal) - 1) * 100).toFixed(1);
    }
  } else {
    if (latestVal !== null && priorVal !== null && priorVal !== 0) {
      row.mom = (((latestVal / priorVal) - 1) * 100).toFixed(1);
    }
  }

  // Calculate Y-o-Y % change by looking exactly 1 year back
  if (sortedEntries.length > 0 && latestVal !== null && latestEntry) {
    const latestDate = new Date(latestEntry.entryWeek || latestEntry.createdAt);

    const oneYearAgoDate = new Date(latestDate);
    oneYearAgoDate.setFullYear(latestDate.getFullYear() - 1);

    // Look for an entry within a reasonable timeframe (14 days for weekly, 30 days for monthly)
    const marginDays = isWeekly ? 14 : 30;
    const oneYearAgoEntry = sortedEntries.find(entry => {
      const entryTarget = entry.entryWeek || entry.createdAt;
      if (!entryTarget) return false;
      const diffInDays = Math.abs(new Date(entryTarget) - oneYearAgoDate) / (1000 * 60 * 60 * 24);
      return diffInDays <= marginDays;
    });

    // Compute YoY percentage difference
    if (oneYearAgoEntry && Number(oneYearAgoEntry.entryValue) !== 0) {
      row.yoy = (((latestVal / Number(oneYearAgoEntry.entryValue)) - 1) * 100).toFixed(1);
    }
  }

  return row;
};

// --- Computed Data Sets for UI ---

// Automatically recalculates whenever filters change
export const filteredWeeklyData = computed(() => {
  const filtered = getFilteredServices('weekly');
  const globalDates = getHistoryDates('weekly');
  return filtered.map(item => processServiceItem(item, 'weekly', globalDates, false));
});

export const monthlyData = computed(() => {
  const filtered = getFilteredServices('monthly');
  const globalDates = getHistoryDates('monthly');
  return filtered.map(item => processServiceItem(item, 'monthly', globalDates, false));
});

export const dataTableRecords = computed(() => {
  const freq = dataTableFrequency.value;
  const filtered = getFilteredServices(freq);
  // Uses dynamically resolved periods from earliest with data up to latest completed
  const globalDates = getHistoryDates(freq);
  return filtered.map(item => processServiceItem(item, freq, globalDates, true));
});


